/**
 * Fonts offered for POI labels (Simple mode → "Chữ địa điểm").
 *
 * - `vietmap`: fontstacks the Vietmap glyph server already has
 *   (maps.vietmap.vn/maps/fonts/{fontstack}/{range}.pbf).
 * - `google`: Google Fonts with a Vietnamese subset. The editor renders their
 *   glyphs (libs/glyph-builder) and uploads them to our API, which serves
 *   them under the same `glyphs` URL as the Vietmap fonts (see
 *   server/src/routes/fonts.ts).
 *
 * The fontstack name is "<Family> <Regular|Bold>".
 */
export type LabelFontWeight = "Regular" | "Bold";

export type LabelFont = {
  family: string;
  source: "vietmap" | "google";
  /** Weights available (Vietmap: the fontstacks that exist). */
  weights: LabelFontWeight[];
  /** For the picker preview. */
  category: "sans" | "serif" | "display";
};

export const LABEL_FONTS: LabelFont[] = [
  { family: "Noto Sans", source: "vietmap", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Roboto", source: "vietmap", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Be Vietnam Pro", source: "google", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Inter", source: "google", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Nunito Sans", source: "google", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Open Sans", source: "google", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Montserrat", source: "google", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Lexend", source: "google", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Mulish", source: "google", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Quicksand", source: "google", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Barlow", source: "google", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Roboto Condensed", source: "google", weights: ["Regular", "Bold"], category: "sans" },
  { family: "Oswald", source: "google", weights: ["Regular", "Bold"], category: "display" },
  { family: "Josefin Sans", source: "google", weights: ["Regular", "Bold"], category: "display" },
  { family: "Lora", source: "google", weights: ["Regular", "Bold"], category: "serif" },
  { family: "Merriweather", source: "google", weights: ["Regular", "Bold"], category: "serif" },
  { family: "Noto Serif", source: "google", weights: ["Regular", "Bold"], category: "serif" },
  { family: "Playfair Display", source: "google", weights: ["Regular", "Bold"], category: "serif" },
];

export const WEIGHT_CSS: Record<LabelFontWeight, number> = { Regular: 400, Bold: 700 };

export function fontstackOf(family: string, weight: LabelFontWeight): string {
  return `${family} ${weight}`;
}

/** "Be Vietnam Pro Bold" → its catalogue entry + weight (null for unknown fontstacks). */
export function parseFontstack(fontstack: string): { font: LabelFont; weight: LabelFontWeight } | null {
  const m = fontstack.match(/^(.*) (Regular|Bold)$/);
  if (!m) return null;
  const font = LABEL_FONTS.find((f) => f.family === m[1]);
  return font ? { font, weight: m[2] as LabelFontWeight } : null;
}

/** Google Fonts CSS URL for `families` (both weights). `text` limits it to a preview subset. */
export function googleFontsCssUrl(families: string[], text?: string): string {
  const fam = families.map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;700`).join("&");
  return `https://fonts.googleapis.com/css2?${fam}&display=block${text ? `&text=${encodeURIComponent(text)}` : ""}`;
}

/**
 * Fontstacks the Vietmap glyph server actually has (probed 2026-10). Any other
 * name makes it answer 400 — and MapLibre then drops the WHOLE vector tile,
 * leaving a blank map. See libs/glyph-guard.
 */
export const VIETMAP_FONTSTACKS = [
  "Noto Sans Regular",
  "Noto Sans Bold",
  "Roboto Regular",
  "Roboto Medium",
  "Roboto Bold",
  "Roboto Condensed Italic",
];
